import {
  Box,
  Collapse,
  HStack,
  Link,
  Spacer,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {LigneMatching} from 'api-types/ffconnect';
import {useTranslation} from 'react-i18next';
import {ArticleBlock} from './ArticleBlock';
import {LigneProduitLayout} from '@/domains/liste/components/LigneProduitLayout';
import {VignetteArticleVStack} from '@/domains/mediatheque/VignetteArticleVStack';
import {MatchingSectionEtatBadge} from '@/backoffice/domains/PdfOrderImport/MatchSectionComponents';
import {RaisonOrpheline, etatDeLaLigne} from './panier-pdf-lignes';
import {TableComparaisonMatching} from './TableComparaisonMatching';

// Ce que la ligne orpheline du bon de commande est devenue.
const LIBELLE_PAR_RAISON: Record<RaisonOrpheline, string> = {
  aucune_correspondance: 'panier.matching_aucune_correspondance',
  article_indisponible: 'panier.matching_article_indisponible',
  supprimee: 'panier.matching_ligne_supprimee',
};

/**
 * Import PDF : ligne du bon de commande qui n'a pas de ligne de panier à
 * afficher — aucun article n'a été identifié, ou la ligne a été retirée du
 * panier depuis l'import. Rendue dans la liste du panier PDF, à son rang dans
 * l'ordre du bon de commande, entre les lignes de panier ordinaires.
 */
export const LigneMatchingOrpheline: React.FC<{
  ligneMatching: LigneMatching;
  raison: RaisonOrpheline;
}> = ({ligneMatching, raison}) => {
  const {t} = useTranslation();
  const {isOpen, onToggle} = useDisclosure();
  const {donnees_extraites, item_matche} = ligneMatching;

  return (
    <LigneProduitLayout
      flexDirection='column'
      px='0'
    >
      {/* Cas 1 : Un article a été identifié (ligne retirée du panier, ou article plus
      disponible au catalogue) : on le présente comme n'importe quelle ligne */}
      {item_matche.tolede ? (
        <ArticleBlock
          code_article={item_matche.tolede.code_article}
          libelleFallback={donnees_extraites.libelle_extrait}
          libelles_tronques
        />
      ) : (
        // Cas 2 : Aucun article identifié : vignette par défaut et libellé du PDF, les seules informations dont on dispose.
        <HStack
          w='100%'
          spacing='4'
          alignItems='center'
        >
          <VignetteArticleVStack article={undefined} />
          <Text fontWeight='600'>{donnees_extraites.libelle_extrait}</Text>
        </HStack>
      )}
      {/* Même bloc de synthèse que sous une ligne de panier, pour que la
      séquence du bon de commande se lise d'une seule traite */}
      <VStack
        align='stretch'
        spacing='0'
        w='100%'
        mt='2'
        bg='gray.50'
        borderRadius='8px'
        overflow='hidden'
      >
        <HStack
          w='100%'
          px='3'
          py='1'
          color='gray.dark'
          bg='gray.light_bg'
        >
          <MatchingSectionEtatBadge etat={etatDeLaLigne(ligneMatching)} />
          <Text fontSize='xs'>{t(LIBELLE_PAR_RAISON[raison])}</Text>
          <Spacer />
          <Link
            onClick={onToggle}
            fontSize='xs'
            textDecoration='underline'
          >
            {t('panier.matching_details')}
          </Link>
        </HStack>
        <Collapse
          in={isOpen}
          animateOpacity
        >
          {/* Cas 1 : La ligne a été supprimée du panier */}
          {item_matche.tolede ? (
            <TableComparaisonMatching ligneMatching={ligneMatching} />
          ) : (
            // Cas 2 : Aucun article identifié : il n'y a rien à comparer.
            // Reste le texte brut du PDF.
            <Box
              px='3'
              py='2'
              fontSize='xs'
              color='gray.dark'
            >
              <Text
                as='span'
                fontWeight='bold'
              >
                {t('back_office.pdf_order_import.match.champ_texte_entree')}{' '}
                :{' '}
              </Text>
              {donnees_extraites.texte_entree}
            </Box>
          )}
        </Collapse>
      </VStack>
    </LigneProduitLayout>
  );
};
